import { TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';

interface DailyStat {
  date: string;
  count: number;
}

interface MonthComparisonPoint {
  day: number;
  primaryCount: number | null;
  comparisonCount: number | null;
}

interface DailyChartPoint {
  date?: string;
  count?: number;
  day?: number;
  primaryCount?: number | null;
  comparisonCount?: number | null;
}

export const DAILY_CHAT_RANGES = [7, 30, 90, 365] as const;
type DailyChatRange = (typeof DAILY_CHAT_RANGES)[number];
type DailyChartView = 'recent' | 'month';

const PRIMARY_SERIES_COLOR = '#56c0a9';
const COMPARISON_SERIES_COLOR = '#cc4fa0';

const monthValue = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const monthDateRange = (month: string) => {
  const [year, monthNumber] = month.split('-').map(Number);
  const lastDay = new Date(year, monthNumber, 0).getDate();
  return {
    date_from: `${month}-01`,
    date_to: `${month}-${String(lastDay).padStart(2, '0')}`
  };
};

export const compareMonthStats = (
  primary: DailyStat[],
  comparison: DailyStat[]
): MonthComparisonPoint[] => {
  const length = Math.max(primary.length, comparison.length);
  return Array.from({ length }, (_, index) => ({
    day: index + 1,
    primaryCount: primary[index]?.count ?? null,
    comparisonCount: comparison[index]?.count ?? null
  }));
};

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    totalThreads: 0,
    totalUsers: 0
  });
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([]);
  const [comparisonStats, setComparisonStats] = useState<DailyStat[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [dailyStatsRange, setDailyStatsRange] = useState<DailyChatRange>(30);
  const [chartView, setChartView] = useState<DailyChartView>('recent');
  const [compareMonths, setCompareMonths] = useState(false);
  const now = useMemo(() => new Date(), []);
  const previousMonth = useMemo(
    () => new Date(now.getFullYear(), now.getMonth() - 1, 1),
    [now]
  );
  const [selectedMonth, setSelectedMonth] = useState(monthValue(now));
  const [comparisonMonth, setComparisonMonth] = useState(
    monthValue(previousMonth)
  );
  const isEnglish = i18n.language.toLowerCase().startsWith('en');
  const locale = isEnglish ? 'en-US' : 'sr-RS';

  const formatMonth = (month: string) =>
    new Date(`${month}-01T12:00:00`).toLocaleDateString(locale, {
      month: 'long',
      year: 'numeric'
    });

  useEffect(() => {
    // Fetch basic stats
    const fetchStats = async () => {
      try {
        // Fetch threads count
        const threadsResponse = await fetch('/api/threads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operation: 'count' })
        });
        const threadsData = await threadsResponse.json();

        // Fetch users count
        const usersResponse = await fetch('/api/threads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operation: 'user_count' })
        });
        const usersData = await usersResponse.json();

        setStats({
          totalThreads: threadsData.count || 0,
          totalUsers: usersData.count || 0
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchDailyStats = async () => {
      setLoadingStats(true);
      try {
        const requestStats = async (body: Record<string, unknown>) => {
          const response = await fetch('/api/threads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operation: 'daily_stats', ...body }),
            signal: controller.signal
          });
          if (!response.ok) {
            throw new Error('Failed to fetch daily chat statistics');
          }
          const data = await response.json();
          return (data.stats || []) as DailyStat[];
        };

        if (chartView === 'recent') {
          setDailyStats(await requestStats({ days: dailyStatsRange }));
          setComparisonStats([]);
        } else {
          const primaryRange = monthDateRange(selectedMonth);
          const requests = [requestStats(primaryRange)];
          if (compareMonths) {
            requests.push(requestStats(monthDateRange(comparisonMonth)));
          }
          const [primary, comparison = []] = await Promise.all(requests);
          setDailyStats(primary);
          setComparisonStats(comparison);
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error fetching daily chat statistics:', error);
          setDailyStats([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoadingStats(false);
      }
    };

    fetchDailyStats();
    return () => controller.abort();
  }, [chartView, compareMonths, comparisonMonth, dailyStatsRange, selectedMonth]);

  const isMonthComparison = chartView === 'month' && compareMonths;
  const chartData: DailyChartPoint[] = isMonthComparison
    ? compareMonthStats(dailyStats, comparisonStats)
    : dailyStats;
  const primaryTotal = dailyStats.reduce((sum, stat) => sum + stat.count, 0);
  const comparisonTotal = comparisonStats.reduce(
    (sum, stat) => sum + stat.count,
    0
  );

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">
        {t('admin.dashboard.title', 'Admin Dashboard')}
      </h1>

      <dl className="mb-8 grid max-w-2xl grid-cols-1 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <div className="px-5 py-4">
          <dt className="text-sm text-muted-foreground">
            {t('admin.dashboard.totalThreads', 'Total Threads')}
          </dt>
          <dd className="mt-1 text-3xl font-semibold tabular-nums tracking-tight">
            {stats.totalThreads.toLocaleString()}
          </dd>
        </div>
        <div className="px-5 py-4">
          <dt className="text-sm text-muted-foreground">
            {t('admin.dashboard.totalUsers', 'Total Users')}
          </dt>
          <dd className="mt-1 text-3xl font-semibold tabular-nums tracking-tight">
            {stats.totalUsers.toLocaleString()}
          </dd>
        </div>
      </dl>

      {/* Daily Chat Statistics Chart */}
      <Card className="mb-8">
        <CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t('admin.dashboard.dailyChats', 'Daily Chat Activity')}
            </CardTitle>
            <CardDescription>
              {t(
                'admin.dashboard.dailyChatsDescription',
                'Number of conversations created each day'
              )}
            </CardDescription>
          </div>
          <div
            className="inline-flex w-fit rounded-lg border border-border bg-muted/30 p-1"
            role="group"
            aria-label={t('admin.dashboard.chartView', 'Chart view')}
          >
            <Button
              type="button"
              size="sm"
              variant={chartView === 'recent' ? 'secondary' : 'ghost'}
              className="h-8 px-3"
              aria-pressed={chartView === 'recent'}
              onClick={() => setChartView('recent')}
            >
              {t('admin.dashboard.recent', 'Recent')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={chartView === 'month' ? 'secondary' : 'ghost'}
              className="h-8 px-3"
              aria-pressed={chartView === 'month'}
              onClick={() => setChartView('month')}
            >
              {t('admin.dashboard.byMonth', 'By month')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6 flex min-h-10 flex-wrap items-end justify-end gap-3">
            {chartView === 'recent' ? (
              <div
                className="inline-flex w-fit rounded-lg border border-border bg-muted/30 p-1"
                role="group"
                aria-label={t('admin.dashboard.chartRange', 'Chart range')}
              >
                {DAILY_CHAT_RANGES.map((days) => (
                  <Button
                    key={days}
                    type="button"
                    size="sm"
                    variant={dailyStatsRange === days ? 'secondary' : 'ghost'}
                    className="h-8 min-w-12 px-3"
                    aria-pressed={dailyStatsRange === days}
                    aria-label={t('admin.dashboard.chartRangeDays', {
                      count: days,
                      defaultValue: `Show the last ${days} days`
                    })}
                    onClick={() => setDailyStatsRange(days)}
                  >
                    {days === 365
                      ? t('admin.dashboard.rangeYear', '1Y')
                      : t('admin.dashboard.rangeDays', {
                          count: days,
                          defaultValue: `${days}D`
                        })}
                  </Button>
                ))}
              </div>
            ) : (
              <>
                <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
                  {t('admin.dashboard.month', 'Month')}
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={(event) => {
                      if (event.target.value) setSelectedMonth(event.target.value);
                    }}
                    className="h-9 w-[170px] text-sm text-foreground"
                  />
                </label>
                {compareMonths ? (
                  <label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
                    {t('admin.dashboard.compareWith', 'Compare with')}
                    <Input
                      type="month"
                      value={comparisonMonth}
                      onChange={(event) => {
                        if (event.target.value) {
                          setComparisonMonth(event.target.value);
                        }
                      }}
                      className="h-9 w-[170px] text-sm text-foreground"
                    />
                  </label>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9"
                  aria-pressed={compareMonths}
                  onClick={() => setCompareMonths((enabled) => !enabled)}
                >
                  {compareMonths
                    ? t('admin.dashboard.singleMonth', 'Single month')
                    : t('admin.dashboard.compareMonths', 'Compare months')}
                </Button>
              </>
            )}
          </div>

          {chartView === 'month' && !loadingStats ? (
            <div className="mb-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span>
                <span
                  className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: PRIMARY_SERIES_COLOR }}
                  aria-hidden="true"
                />
                {formatMonth(selectedMonth)}:{' '}
                <strong className="font-semibold text-foreground">
                  {primaryTotal.toLocaleString()}
                </strong>
              </span>
              {isMonthComparison ? (
                <span>
                  <span
                    className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: COMPARISON_SERIES_COLOR }}
                    aria-hidden="true"
                  />
                  {formatMonth(comparisonMonth)}:{' '}
                  <strong className="font-semibold text-foreground">
                    {comparisonTotal.toLocaleString()}
                  </strong>
                </span>
              ) : null}
            </div>
          ) : null}

          {loadingStats ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              {t('admin.dashboard.loading', 'Loading...')}
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              {t('admin.dashboard.noData', 'No data available')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey={isMonthComparison ? 'day' : 'date'}
                  tickFormatter={(value) => {
                    if (isMonthComparison) return String(value);
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  minTickGap={28}
                  className="text-xs"
                />
                <YAxis className="text-xs" />
                <Tooltip
                  labelFormatter={(value) => {
                    if (
                      typeof value !== 'string' &&
                      typeof value !== 'number'
                    ) {
                      return '';
                    }
                    if (isMonthComparison) {
                      return `${t('admin.dashboard.day', 'Day')} ${value}`;
                    }
                    const date = new Date(value);
                    return date.toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                  }}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px'
                  }}
                />
                {isMonthComparison ? (
                  <>
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="primaryCount"
                      stroke={PRIMARY_SERIES_COLOR}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{
                        fill: PRIMARY_SERIES_COLOR,
                        stroke: 'hsl(var(--background))',
                        strokeWidth: 2,
                        r: 5
                      }}
                      connectNulls={false}
                      name={formatMonth(selectedMonth)}
                    />
                    <Line
                      type="monotone"
                      dataKey="comparisonCount"
                      stroke={COMPARISON_SERIES_COLOR}
                      strokeWidth={2.5}
                      strokeDasharray="7 5"
                      dot={false}
                      activeDot={{
                        fill: COMPARISON_SERIES_COLOR,
                        stroke: 'hsl(var(--background))',
                        strokeWidth: 2,
                        r: 5
                      }}
                      connectNulls={false}
                      name={formatMonth(comparisonMonth)}
                    />
                  </>
                ) : (
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke={PRIMARY_SERIES_COLOR}
                    strokeWidth={2}
                    dot={
                      chartView === 'month' || dailyStatsRange <= 30
                        ? { fill: PRIMARY_SERIES_COLOR, r: 4 }
                        : false
                    }
                    activeDot={{ r: 6 }}
                    name={
                      chartView === 'month'
                        ? formatMonth(selectedMonth)
                        : t('admin.dashboard.chats', 'Chats')
                    }
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default AdminDashboard;
